import React, { InputHTMLAttributes, forwardRef } from 'react';
import { InputText, InputTextProps } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { AppTheme } from '../../AppTheme';

// Create a modified version of InputHTMLAttributes that excludes the 'value' property
type BaseInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value'>;

interface InputProps extends BaseInputProps {
  label?: string;
  helpText?: string;
  error?: string;
  value?: string | null; // Match InputText's value type
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, helpText, error, className = '', ...props }, ref) => {
    const inputClassName = classNames(
      'w-full transition-colors border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50',
      error ? 'p-invalid border-red-500 focus:border-red-500 focus:ring-red-200' : '',
      props.disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : '',
      className
    );

    const containerClassName = classNames(
      'flex flex-col gap-2'
    );

    return (
      <div className={containerClassName}>
        {label && (
          <label 
            htmlFor={props.id} 
            className={`text-sm font-medium ${props.disabled ? 'text-gray-500' : 'text-gray-700'}`}
          >
            {label}
          </label>
        )}
        <InputText
          ref={ref}
          className={inputClassName}
          style={{
            height: '40px',
            borderRadius: '6px'
          }}
          {...props as InputTextProps} // Type assertion to InputTextProps
        />
        {(helpText || error) && (
          <small className={`text-xs ${error ? 'text-red-500' : 'text-gray-500'}`}>
            {error || helpText}
          </small>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';